import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { TriangleAlert } from "lucide-react";
import { type Dispatch, type UnknownAction } from "redux";

import {
  addAdministrativeAreasDetails as addAdministrativeAreasDetailsAction,
  administrativeAreasDetails as administrativeAreasDetailsAction,
  administrativeAreasDetailsReset as administrativeAreasDetailsResetAction,
  listAdministrativeAreas as listAdministrativeAreasAction,
  updateAdministrativeAreasDetails as updateAdministrativeAreasDetailsAction,
} from "@/actions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { ALL_ITEMS_PAGE_SIZE } from "@/constants/pagination";
import { getCountryObjectFromCache } from "@/utils";
import { adaptInitialFormValues } from "@/utils/wfpManagerConfigurationUtils";

import { ADMINISTRATIVE_AREA_FORM_ID, type AdministrativeAreaFormValues, Form } from "../Form";
import {
  type AdministrativeArea,
  type AdministrativeAreasModalOptions,
  type AdministrativeAreaType,
  type EntityId,
  type SetAdministrativeAreasModalOptions,
} from "../../types";

type AdministrativeAreaInitialValues =
  | AdministrativeArea
  | { results?: AdministrativeArea[] }
  | null
  | undefined;

interface AdministrativeAreasDetailState {
  data: AdministrativeAreaInitialValues;
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  administrativeAreasDetail: AdministrativeAreasDetailState;
}

interface StateProps {
  errorMessage: string;
  initialFormValues?: AdministrativeAreaInitialValues;
  isFetching: boolean;
}

interface OwnProps {
  administrativeAreas?: AdministrativeArea[];
  administrativeAreaTypes?: AdministrativeAreaType[];
  modalOptions?: AdministrativeAreasModalOptions;
  setModalOptions: SetAdministrativeAreasModalOptions;
}

interface DispatchProps {
  addAdministrativeAreasDetails: (values: AdministrativeArea) => Promise<unknown>;
  administrativeAreasDetails: (id: EntityId) => void;
  administrativeAreasDetailsReset: () => void;
  refreshAdministrativeAreas: () => void;
  updateAdministrativeAreasDetails: (id: EntityId, values: AdministrativeArea) => Promise<unknown>;
}

type AdministrativeAreasEditModalProps = StateProps & OwnProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const CLOSED_MODAL_OPTIONS: AdministrativeAreasModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const dispatchRequest = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

const isEntityId = (id: AdministrativeAreasModalOptions["id"]): id is EntityId =>
  id !== undefined && id !== "add";

const getCurrentCountryId = () => {
  const country = getCountryObjectFromCache() as { id?: EntityId | null } | null | undefined;

  return country?.id;
};

export const adaptFormValues = (values: AdministrativeAreaFormValues): AdministrativeArea => ({
  ...values,
  country: getCurrentCountryId(),
});

export const formatInitialValues = (
  initialFormValues: AdministrativeAreaInitialValues,
  modalOptions: AdministrativeAreasModalOptions
): Partial<AdministrativeAreaFormValues> | undefined => {
  const data = adaptInitialFormValues(initialFormValues, modalOptions.id) as
    | AdministrativeArea
    | undefined;

  if (!data) {
    return undefined;
  }

  return {
    active: Boolean(data.active),
    country: data.country ?? undefined,
    id: data.id ?? undefined,
    name: data.name ?? "",
    parent: data.parent ?? null,
    type: data.type ?? null,
  };
};

export const AdministrativeAreasEditModal = ({
  administrativeAreas = [],
  administrativeAreaTypes = [],
  initialFormValues,
  isFetching,
  modalOptions = CLOSED_MODAL_OPTIONS,
  setModalOptions,
  addAdministrativeAreasDetails,
  administrativeAreasDetails,
  administrativeAreasDetailsReset,
  refreshAdministrativeAreas,
  updateAdministrativeAreasDetails,
}: AdministrativeAreasEditModalProps) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);
  const isViewMode = modalOptions.type === "view";
  const initialValues = useMemo(
    () =>
      modalOptions.type === "create"
        ? undefined
        : formatInitialValues(initialFormValues, modalOptions),
    [initialFormValues, modalOptions]
  );

  const handleClose = () => {
    setHasSaveError(false);
    setModalOptions(CLOSED_MODAL_OPTIONS);
    administrativeAreasDetailsReset();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen || isSaving) {
      return;
    }

    handleClose();
  };

  const preventCloseWhileSaving = (event: Event) => {
    if (isSaving) {
      event.preventDefault();
    }
  };

  const handleSubmit = async (formValues: AdministrativeAreaFormValues) => {
    const values = adaptFormValues(formValues);

    setHasSaveError(false);
    setIsSaving(true);

    const result =
      modalOptions.type === "edit" && isEntityId(modalOptions.id)
        ? await updateAdministrativeAreasDetails(modalOptions.id, values)
        : await addAdministrativeAreasDetails(values);

    setIsSaving(false);

    if (!result) {
      setHasSaveError(true);
      return;
    }

    refreshAdministrativeAreas();
    handleClose();
  };

  useEffect(() => {
    if (!modalOptions.isOpen || modalOptions.type === "create" || !isEntityId(modalOptions.id)) {
      return;
    }

    administrativeAreasDetails(modalOptions.id);
  }, [administrativeAreasDetails, modalOptions.id, modalOptions.isOpen, modalOptions.type]);

  return (
    <Dialog open={Boolean(modalOptions.isOpen)} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-xl"
        aria-describedby={undefined}
        showCloseButton={!isSaving}
        onEscapeKeyDown={preventCloseWhileSaving}
        onPointerDownOutside={preventCloseWhileSaving}
      >
        <DialogHeader>
          <DialogTitle>{t("administrativeAreas.modal.title")}</DialogTitle>
        </DialogHeader>
        {hasSaveError ? (
          <Alert variant="destructive">
            <TriangleAlert aria-hidden="true" />
            <AlertDescription>{t("errorMessage.title")}</AlertDescription>
          </Alert>
        ) : null}
        <Form
          administrativeAreas={administrativeAreas}
          administrativeAreaTypes={administrativeAreaTypes}
          initialValues={initialValues}
          isFetching={isFetching}
          modalOptions={modalOptions}
          onSubmit={handleSubmit}
        />
        <DialogFooter>
          {isViewMode ? (
            <WFPButton type="button" onClick={handleClose}>
              {t("common.back")}
            </WFPButton>
          ) : (
            <>
              <WFPButton type="button" variant="outline" disabled={isSaving} onClick={handleClose}>
                {t("common.cancel")}
              </WFPButton>
              <WFPButton
                type="submit"
                form={ADMINISTRATIVE_AREA_FORM_ID}
                disabled={isSaving || isFetching}
              >
                {isSaving && <Spinner className="size-4" />}
                {t("common.save")}
              </WFPButton>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    initialFormValues: state.administrativeAreasDetail.data,
    isFetching: state.administrativeAreasDetail.isFetching,
    errorMessage: state.administrativeAreasDetail.errorMessage,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    administrativeAreasDetails: id =>
      dispatchAction(dispatch, administrativeAreasDetailsAction(String(id))),
    administrativeAreasDetailsReset: () =>
      dispatchAction(dispatch, administrativeAreasDetailsResetAction()),
    addAdministrativeAreasDetails: values =>
      dispatchRequest(dispatch, addAdministrativeAreasDetailsAction(values)),
    updateAdministrativeAreasDetails: (id, values) =>
      dispatchRequest(dispatch, updateAdministrativeAreasDetailsAction(id, values)),
    refreshAdministrativeAreas: () =>
      dispatchAction(
        dispatch,
        listAdministrativeAreasAction({
          page_size: ALL_ITEMS_PAGE_SIZE,
          page: 1,
          country: getCurrentCountryId(),
        })
      ),
  };
};

export const ConnectedAdministrativeAreasEditModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(AdministrativeAreasEditModal);
